/**
 * Vérification OCR Correction V2
 * Image + note superposées pour comparaison rapide
 * Raccourcis : ↑↓ navigation, Entrée confirmer, E éditer, Échap annuler
 */

import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  useGetLignesOCRManuscritV2Query,
  useGetImageLigneOCRV2Query,
  useLazyGetCodesManquantsOCRV2Query,
  useValiderLigneOCRManuscritV2Mutation,
  useValiderToutReleveManuscritV2Mutation,
  useRenvoyerNotesReleveV2Mutation,
  useLancerOCRManuscritV2Mutation,
  useSupprimerLigneOCRV2Mutation,
  useModifierCodeLigneOCRV2Mutation,
  useAjouterCopieReleveMutation,
  ocrApiV2,
  LigneOCRManuscritV2,
} from '../../services/ocrApiV2';
import { useGetRelevesCorrectionQuery, ocrApi } from '../../services/ocrApi';
import { useLazyRechercherCopieParCodeQuery, CopieExamen } from '../../services/copiesApi';
import {
  CheckCircle2, AlertTriangle, Save, X, Eye, FileCheck, Loader,
  ChevronLeft, ThumbsUp, ZoomIn, ZoomOut, FileImage, Keyboard, RefreshCw,
  Trash2, Edit3, Plus,
} from 'lucide-react';
import { formatNote } from '../../utils/formatNote';

const getConfianceColor = (conf: number) => {
  const c = Number(conf);
  if (c >= 90) return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
  if (c >= 70) return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
  return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
};

// Helper pour extraire les infos étudiant depuis les différents formats
const getEtudiantInfo = (l: LigneOCRManuscritV2) => {
  const detail = l.copie_examen_detail || l.copie_examen;
  const etud = detail?.etudiant;
  return {
    nom: etud?.nom || l.etudiant_nom?.split(' ')[0] || null,
    prenoms: etud?.prenoms || l.etudiant_nom?.split(' ').slice(1).join(' ') || null,
    nomComplet: etud ? `${etud.nom} ${etud.prenoms}` : l.etudiant_nom || null,
    matricule: etud?.matricule || l.etudiant_matricule || null,
    classe: etud?.classe || l.etudiant_classe || null,
    exists: !!(etud || l.etudiant_nom),
  };
};

const VerificationOCRCorrectionV2: React.FC = () => {
  const { releveId } = useParams<{ releveId: string }>();
  const navigate = useNavigate();
  const [filtreAVerifier, setFiltreAVerifier] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showFullImage, setShowFullImage] = useState(false);
  const [showRaccourcis, setShowRaccourcis] = useState(false);
  const [editNote, setEditNote] = useState('');
  const [editCommentaire, setEditCommentaire] = useState('');
  const [enEdition, setEnEdition] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  const { data: releves } = useGetRelevesCorrectionQuery();
  const { data: toutesLignes, isLoading, refetch } = useGetLignesOCRManuscritV2Query(
    { releveId: parseInt(releveId!), aVerifier: false },
    { skip: !releveId }
  );

  const [validerLigne] = useValiderLigneOCRManuscritV2Mutation();
  const [validerTout, { isLoading: validerToutLoading }] = useValiderToutReleveManuscritV2Mutation();
  const [renvoyerNotes, { isLoading: renvoyerLoading }] = useRenvoyerNotesReleveV2Mutation();
  const [lancerOCR, { isLoading: ocrLoading }] = useLancerOCRManuscritV2Mutation();
  const [supprimerLigne, { isLoading: supprimerLoading }] = useSupprimerLigneOCRV2Mutation();
  const [modifierCode, { isLoading: modifierLoading }] = useModifierCodeLigneOCRV2Mutation();
  const [ajouterCopie, { isLoading: ajouterLoading }] = useAjouterCopieReleveMutation();
  const [rechercherCopie] = useLazyRechercherCopieParCodeQuery();
  const [fetchCodesManquants, { data: codesManquantsData, isFetching: codesManquantsLoading }] = useLazyGetCodesManquantsOCRV2Query();

  // États pour actions sur codes
  const [lignePourSuppression, setLignePourSuppression] = useState<LigneOCRManuscritV2 | null>(null);
  const [lignePourEdition, setLignePourEdition] = useState<LigneOCRManuscritV2 | null>(null);
  const [nouveauCode, setNouveauCode] = useState('');
  const [showAjoutManuel, setShowAjoutManuel] = useState(false);
  const [ajoutCode, setAjoutCode] = useState('');
  const [ajoutNote, setAjoutNote] = useState('');
  const [ajoutAbsent, setAjoutAbsent] = useState(false);
  const [ajoutPreview, setAjoutPreview] = useState<CopieExamen | null>(null);
  const [ajoutPreviewError, setAjoutPreviewError] = useState<string | null>(null);
  const [ajoutPreviewLoading, setAjoutPreviewLoading] = useState(false);
  const ajoutNoteRef = useRef<HTMLInputElement>(null);
  const [showConfirmTerminer, setShowConfirmTerminer] = useState(false);
  const [showRenvoyerModal, setShowRenvoyerModal] = useState(false);
  const [renvoyerEcraser, setRenvoyerEcraser] = useState(false);
  const [renvoyerResult, setRenvoyerResult] = useState<null | {
    nb_crees: number; nb_modifies: number; nb_ignores: number;
    nb_total_traites: number; recalcul_effectue: number;
  }>(null);
  const [nbCodesPapier, setNbCodesPapier] = useState('');
  const [rechercheCode, setRechercheCode] = useState('');
  // IDs supprimés localement — garantit un filtrage UI instantané indépendamment de RTK Query
  const [deletedIds, setDeletedIds] = useState<Set<number>>(new Set());
  // Surcharges locales de notes (après confirmer/corriger) — affichage instantané garanti
  const [noteOverrides, setNoteOverrides] = useState<Record<number, { note_finale?: number | null; absent_valide?: boolean; verifie?: boolean; necessite_verification?: boolean; source_note?: string; commentaire?: string }>>({});
  // Modal "codes manquants"
  const [showCodesManquants, setShowCodesManquants] = useState(false);
  const ajoutInputRef = useRef<HTMLInputElement>(null);

  // États du polling OCR (pendant que Celery traite en arrière-plan)
  const [ocrPolling, setOcrPolling] = useState(false);
  const [ocrElapsed, setOcrElapsed] = useState(0);
  // Garde contre double-trigger du polling auto au mount
  const autoPollingTriggered = useRef(false);

  // Démarre le polling des lignes OCR (utilisable sans relancer l'OCR).
  // Utile quand l'OCR est déjà en cours (lancé depuis la page précédente) :
  // on attache juste la progression à la DB et on arrête dès que les lignes
  // apparaissent, ou au bout de 120s.
  const demarrerPollingOCR = useCallback(() => {
    setOcrPolling(true);
    setOcrElapsed(0);

    const POLL_INTERVAL_MS = 4000;
    const MAX_DURATION_S = 120;
    const startedAt = Date.now();

    const timer = setInterval(async () => {
      const elapsed = Math.round((Date.now() - startedAt) / 1000);
      setOcrElapsed(elapsed);

      if (elapsed > MAX_DURATION_S) {
        clearInterval(timer);
        setOcrPolling(false);
        setMessage({
          type: 'error',
          text: "OCR toujours en cours après 2 min — vérifiez les logs serveur ou retournez le relevé en cours.",
        });
        return;
      }

      try {
        const result = await refetch().unwrap();
        const nbLignes = Array.isArray(result) ? result.length : 0;
        if (nbLignes > 0) {
          clearInterval(timer);
          setOcrPolling(false);
          setMessage({
            type: 'success',
            text: `OCR terminé en ${elapsed}s : ${nbLignes} ligne(s) détectée(s).`,
          });
        }
      } catch {
        /* refetch raté ponctuellement — on continue */
      }
    }, POLL_INTERVAL_MS);
  }, [refetch]);

  const handleRelancerOCR = useCallback(async () => {
    if (!releveId || ocrPolling) return;
    setMessage(null);
    try {
      await lancerOCR(parseInt(releveId)).unwrap();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.error || err?.data?.message || "Échec du lancement OCR" });
      return;
    }
    demarrerPollingOCR();
  }, [releveId, lancerOCR, ocrPolling, demarrerPollingOCR]);

  const handleSupprimerLigne = useCallback(() => {
    if (!lignePourSuppression || !releveId) return;
    const ligneId = lignePourSuppression.id;
    const code = lignePourSuppression.code_copie_detecte;

    // ── 1. Feedback IMMÉDIAT via state React (garanti synchrone) ──
    setDeletedIds(prev => {
      const next = new Set(prev);
      next.add(ligneId);
      return next;
    });
    setLignePourSuppression(null);
    setSelectedIndex(i => Math.max(0, i - 1));
    setMessage({ type: 'success', text: `Code ${code} supprimé` });

    // ── 2. Mutation en arrière-plan (fire-and-forget) ──
    supprimerLigne({ releveId: parseInt(releveId), ligneId })
      .unwrap()
      .catch((err: any) => {
        // Rollback : restaurer la ligne en retirant l'ID de deletedIds
        setDeletedIds(prev => {
          const next = new Set(prev);
          next.delete(ligneId);
          return next;
        });
        setMessage({ type: 'error', text: err?.data?.error || 'Erreur suppression — la ligne a été restaurée' });
      });
  }, [lignePourSuppression, releveId, supprimerLigne]);

  const handleModifierCode = useCallback(async () => {
    if (!lignePourEdition || !releveId || !nouveauCode.trim()) return;
    try {
      const res = await modifierCode({
        releveId: parseInt(releveId),
        ligneId: lignePourEdition.id,
        nouveauCode: nouveauCode.trim(),
      }).unwrap();
      setMessage({
        type: res.matched ? 'success' : 'error',
        text: res.message,
      });
      setLignePourEdition(null);
      setNouveauCode('');
      await refetch();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.error || 'Erreur modification code' });
    }
  }, [lignePourEdition, releveId, nouveauCode, modifierCode, refetch]);

  const handleAjouterManuel = useCallback(async () => {
    if (!releveId || !ajoutCode.trim()) return;
    if (!ajoutAbsent && !ajoutNote.trim()) {
      setMessage({ type: 'error', text: 'Saisissez la note (ou cochez Absent)' });
      return;
    }
    // Validation bornes : note ∈ [0, 20]
    if (!ajoutAbsent) {
      const noteParsed = parseFloat(ajoutNote);
      if (isNaN(noteParsed) || noteParsed < 0 || noteParsed > 20) {
        setMessage({ type: 'error', text: `Note invalide : ${ajoutNote} (doit être entre 0 et 20)` });
        return;
      }
    }
    const code = ajoutCode.trim();
    // Détection locale du conflit de matière (preview vs relevé courant)
    const releveActuel = releves?.find(r => r.id === parseInt(releveId));
    const matiereConflit = !!(
      ajoutPreview?.matiere &&
      releveActuel?.matiere &&
      ajoutPreview.matiere !== releveActuel.matiere
    );

    const payload: any = {
      releveId: parseInt(releveId),
      code_copie: code,
    };
    if (ajoutAbsent) {
      payload.absent = true;
    } else {
      payload.note_finale = parseFloat(ajoutNote);
    }
    // On envoie force=true si on a détecté le conflit dans la preview
    // (l'utilisateur a vu la matière différente et a quand même cliqué Ajouter)
    if (matiereConflit) {
      payload.force = true;
    }

    const resetForm = () => {
      setAjoutCode('');
      setAjoutNote('');
      setAjoutAbsent(false);
      setAjoutPreview(null);
      setAjoutPreviewError(null);
      setTimeout(() => ajoutInputRef.current?.focus(), 50);
    };

    // Optimistic : insérer la ligne localement pour un feedback immédiat
    // (sera remplacée par les données réelles après la réponse serveur)
    const tempId = -Date.now(); // ID négatif temporaire
    const queryArgs = { releveId: parseInt(releveId), aVerifier: false };
    const optimisticPatch = dispatch(
      ocrApiV2.util.updateQueryData('getLignesOCRManuscritV2' as any, queryArgs, (draft: any) => {
        const existing = (draft as LigneOCRManuscritV2[]);
        const lastNum = existing.length > 0 ? Math.max(...existing.map(l => l.numero_ligne || 0)) : 0;
        const newLine: any = {
          id: tempId,
          releve: parseInt(releveId),
          numero_ligne: lastNum + 1,
          numero_page: 1,
          code_copie_detecte: code,
          confiance_code: 100,
          confiance_manuscrit: 100,
          confiance_finale: 100,
          note_finale: ajoutAbsent ? null : parseFloat(ajoutNote),
          absent_valide: ajoutAbsent,
          absent_detecte: ajoutAbsent,
          source_note: 'manuel',
          necessite_verification: false,
          verifie: true,
          raison_verification: 'Ajout manuel',
          commentaire: 'En cours...',
          etudiant_nom: ajoutPreview?.etudiant_nom || '',
          etudiant_matricule: ajoutPreview?.etudiant_matricule || '',
          etudiant_classe: ajoutPreview?.etudiant_classe || '',
          copie_examen: ajoutPreview?.id || undefined,
          image_base64: '',
          erreur: '',
          cases_detectees: null,
          note_cases_detectee: null,
          note_manuscrite_detectee: null,
          confiance_cases: 0,
          verifie_par: undefined,
          date_verification: undefined,
        };
        existing.push(newLine);
      }) as any
    );

    // ── Feedback IMMÉDIAT ── on ferme le form + message avant même le serveur
    setMessage({ type: 'success', text: `Copie ${code} ajoutée` });
    resetForm();

    // ── Mutation en arrière-plan (fire-and-forget) ──
    ajouterCopie(payload)
      .unwrap()
      .then(() => {
        // Succès : le refetch automatique (invalidatesTags) remplacera la ligne optimiste par la vraie
      })
      .catch(async (err: any) => {
        const errData = err?.data || {};
        // Cas où le backend détecte un conflit qu'on n'avait pas anticipé : on force et réessaie
        if (errData.conflit && !payload.force) {
          try {
            await ajouterCopie({ ...payload, force: true }).unwrap();
            setMessage({ type: 'success', text: 'Ajouté avec réassociation' });
            return;
          } catch (err2: any) {
            optimisticPatch.undo();
            setMessage({ type: 'error', text: err2?.data?.error || 'Erreur lors de la réassociation' });
            return;
          }
        }
        // Rollback visuel si erreur définitive
        optimisticPatch.undo();
        setMessage({ type: 'error', text: errData.error || errData.message || 'Erreur lors de l\'ajout — ligne retirée' });
      });
  }, [releveId, ajoutCode, ajoutNote, ajoutAbsent, ajouterCopie, dispatch, ajoutPreview, releves]);

  const releve = releves?.find(r => r.id === parseInt(releveId!));

  // Auto-démarre le polling dès le 1er affichage si l'OCR tourne encore côté backend.
  // Cas typique : l'utilisateur arrive depuis "Générer la correction" où il a cliqué
  // "Vérifier" — l'OCR a été lancé mais les lignes ne sont pas encore en base.
  //   - status 'scanne'  → OCR en cours (non fini)  → on poll
  //   - status 'traite' sans lignes → OCR fini, réellement 0 ligne → on n'auto-poll pas
  useEffect(() => {
    if (autoPollingTriggered.current) return;
    if (isLoading || ocrPolling) return;
    if (!releve || !toutesLignes) return;
    if (toutesLignes.length > 0) return;
    if (releve.status !== 'scanne') return;
    autoPollingTriggered.current = true;
    demarrerPollingOCR();
  }, [isLoading, ocrPolling, releve, toutesLignes, demarrerPollingOCR]);

  // Lignes enrichies des overrides locaux (notes corrigées, validations) pour affichage instantané
  const lignes = React.useMemo<LigneOCRManuscritV2[]>(() => {
    const base = toutesLignes || [];
    if (Object.keys(noteOverrides).length === 0) return base;
    return base.map(l => {
      const ov = noteOverrides[l.id];
      return ov ? ({ ...l, ...ov } as LigneOCRManuscritV2) : l;
    });
  }, [toutesLignes, noteOverrides]);

  // Filtre calculé (useMemo évite les recalculs inutiles)
  // Inclut deletedIds pour masquer instantanément les lignes supprimées localement
  const lignesFiltrees = React.useMemo(() => {
    let result = lignes.filter(l => !deletedIds.has(l.id));
    if (filtreAVerifier) result = result.filter(l => l.necessite_verification);
    const q = (rechercheCode || '').trim().toLowerCase();
    if (q) {
      result = result.filter(l => {
        const code = String(l.code_copie_detecte || '').toLowerCase();
        if (code.includes(q)) return true;
        const etud = getEtudiantInfo(l);
        const nom = String(etud.nomComplet || '').toLowerCase();
        if (nom.includes(q)) return true;
        const matricule = String(etud.matricule || '').toLowerCase();
        if (matricule.includes(q)) return true;
        const classe = String(etud.classe || '').toLowerCase();
        if (classe.includes(q)) return true;
        return false;
      });
    }
    return result;
  }, [lignes, filtreAVerifier, rechercheCode, deletedIds]);

  const ligneCourante = lignesFiltrees[selectedIndex];
  // Ignorer les lignes supprimées localement dans les compteurs
  const lignesVivantes = React.useMemo(
    () => lignes.filter(l => !deletedIds.has(l.id)),
    [lignes, deletedIds]
  );
  const nbAVerifier = lignesVivantes.filter(l => l.necessite_verification).length;
  const nbValidees = lignesVivantes.filter(l => !l.necessite_verification).length;

  useEffect(() => { setSelectedIndex(0); }, [filtreAVerifier, rechercheCode]);

  // Prefetch des images des lignes voisines (±2) pour navigation instantanée
  const prefetchImage = ocrApiV2.usePrefetch('getImageLigneOCRV2');
  useEffect(() => {
    if (!releveId || lignesFiltrees.length === 0) return;
    const rid = parseInt(releveId);
    // Prefetch ±2 autour de la ligne courante (silencieux, mise en cache)
    const offsets = [1, 2, -1, -2];
    for (const off of offsets) {
      const idx = selectedIndex + off;
      if (idx < 0 || idx >= lignesFiltrees.length) continue;
      const l = lignesFiltrees[idx];
      if (!l || l.id < 0) continue; // skip lignes optimistes
      // Pas de force=true → respecte le cache, évite requêtes répétées
      prefetchImage({ releveId: rid, ligneId: l.id });
    }
  }, [selectedIndex, lignesFiltrees, releveId, prefetchImage]);

  // Debounce pour la recherche de copie dans le modal d'ajout
  // - min 6 caractères pour éviter les 404 inutiles pendant la frappe
  // - debounce 600ms (les scanners envoient tout d'un coup, les humains tapent + lentement)
  useEffect(() => {
    if (!showAjoutManuel) return;
    const code = ajoutCode.trim();
    // Reset preview si champ vide
    if (!code) {
      setAjoutPreview(null);
      setAjoutPreviewError(null);
      setAjoutPreviewLoading(false);
      return;
    }
    // Attendre que l'utilisateur ait saisi un code assez long
    const CODE_MIN_LEN = 6;
    if (code.length < CODE_MIN_LEN) {
      setAjoutPreview(null);
      setAjoutPreviewError(null);
      setAjoutPreviewLoading(false);
      return;
    }
    setAjoutPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const copie = await rechercherCopie(code).unwrap();
        setAjoutPreview(copie);
        setAjoutPreviewError(null);
        // Auto-focus sur le champ note après détection
        if (copie?.etudiant) {
          setTimeout(() => ajoutNoteRef.current?.focus(), 80);
        }
      } catch (err: any) {
        setAjoutPreview(null);
        // Distinction entre 404 (code pas encore complet) et autres erreurs
        if (err?.status === 404) {
          setAjoutPreviewError(`Code "${code}" introuvable dans la base`);
        } else {
          setAjoutPreviewError(err?.data?.error || 'Erreur réseau lors de la recherche');
        }
      } finally {
        setAjoutPreviewLoading(false);
      }
    }, 600);
    return () => { clearTimeout(timer); setAjoutPreviewLoading(false); };
  }, [ajoutCode, showAjoutManuel, rechercherCopie]);
  useEffect(() => {
    if (ligneCourante) {
      setEditNote(ligneCourante.note_finale?.toString() ?? '');
      setEditCommentaire(ligneCourante.commentaire || '');
      setEnEdition(false);
    }
  }, [ligneCourante?.id]);
  useEffect(() => { if (message) { const t = setTimeout(() => setMessage(null), 3000); return () => clearTimeout(t); } }, [message]);
  useEffect(() => {
    const el = document.getElementById(`ligne-item-${selectedIndex}`);
    const container = document.getElementById('ligne-list-container');
    if (!el || !container) return;
    // Scroll uniquement dans le conteneur, sans déclencher le scroll de la page principale
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    if (elRect.top < containerRect.top) {
      container.scrollTop += elRect.top - containerRect.top;
    } else if (elRect.bottom > containerRect.bottom) {
      container.scrollTop += elRect.bottom - containerRect.bottom;
    }
  }, [selectedIndex]);

  const goNext = useCallback(() => setSelectedIndex(i => Math.min(lignesFiltrees.length - 1, i + 1)), [lignesFiltrees.length]);
  const goPrev = useCallback(() => setSelectedIndex(i => Math.max(0, i - 1)), []);

  const handleConfirmer = useCallback(() => {
    if (!ligneCourante) return;
    if (ligneCourante.note_finale === null || ligneCourante.note_finale === undefined) {
      setEnEdition(true); setTimeout(() => inputRef.current?.focus(), 50); return;
    }
    const ligneId = ligneCourante.id;
    const noteVal = parseFloat(String(ligneCourante.note_finale));

    // Validation bornes : note ∈ [0, 20]
    if (isNaN(noteVal) || noteVal < 0 || noteVal > 20) {
      setMessage({ type: 'error', text: `Note invalide : ${noteVal} (doit être entre 0 et 20)` });
      return;
    }

    // ── 1. UI instantanée via state local ──
    setNoteOverrides(prev => ({
      ...prev,
      [ligneId]: {
        note_finale: noteVal,
        verifie: true,
        necessite_verification: false,
        source_note: 'manuel',
      },
    }));
    setMessage({ type: 'success', text: `${noteVal}/20 confirmé` });
    goNext();

    // ── 2. Mutation en arrière-plan ──
    validerLigne({ releveId: parseInt(releveId!), ligneId, note_finale: noteVal, commentaire: '' })
      .unwrap()
      .catch(() => {
        // Rollback : retirer l'override
        setNoteOverrides(prev => {
          const next = { ...prev };
          delete next[ligneId];
          return next;
        });
        setMessage({ type: 'error', text: 'Erreur de confirmation — note restaurée' });
      });
  }, [ligneCourante, validerLigne, releveId, goNext]);

  const handleSauvegarder = useCallback(() => {
    if (!ligneCourante) return;
    const note = parseFloat(editNote);
    if (isNaN(note)) return;

    // Validation bornes : note ∈ [0, 20]
    if (note < 0 || note > 20) {
      setMessage({ type: 'error', text: `Note invalide : ${note} (doit être entre 0 et 20)` });
      return;
    }

    const ligneId = ligneCourante.id;
    const commentaireLocal = editCommentaire;

    // ── 1. UI instantanée via state local ──
    setNoteOverrides(prev => ({
      ...prev,
      [ligneId]: {
        note_finale: note,
        commentaire: commentaireLocal,
        verifie: true,
        necessite_verification: false,
        source_note: 'manuel',
      },
    }));
    setMessage({ type: 'success', text: `Corrigé : ${note}/20` });
    setEnEdition(false);
    goNext();

    // ── 2. Mutation en arrière-plan ──
    validerLigne({ releveId: parseInt(releveId!), ligneId, note_finale: note, commentaire: commentaireLocal })
      .unwrap()
      .catch(() => {
        setNoteOverrides(prev => {
          const next = { ...prev };
          delete next[ligneId];
          return next;
        });
        setMessage({ type: 'error', text: 'Erreur de correction — note restaurée' });
      });
  }, [ligneCourante, editNote, editCommentaire, validerLigne, releveId, goNext]);

  const handleValiderTout = async () => {
    if (!window.confirm('Valider toutes les notes et les enregistrer ?')) return;
    try {
      await validerTout(parseInt(releveId!)).unwrap();
      // Invalider le cache de ocrApi (qui contient getRelevesCorrection utilisé par CETTE page)
      // car la mutation appartient à ocrApiV2 → invalidatesTags ne traverse pas les APIs RTK séparées.
      // Sans ça, le statut affiché en haut reste obsolète tant qu'on ne recharge pas la page.
      dispatch(ocrApi.util.invalidateTags(['ReleveCorrection']));
      setMessage({ type: 'success', text: 'Toutes les notes validées !' });
      setTimeout(() => navigate('/ocr/generation-correction-v2'), 1500);
    } catch (error: any) { setMessage({ type: 'error', text: error?.data?.message || 'Erreur' }); }
  };

  const handleRenvoyerNotes = async () => {
    if (!releveId) return;
    setRenvoyerResult(null);
    try {
      const res = await renvoyerNotes({ releveId: parseInt(releveId), ecraser: renvoyerEcraser }).unwrap();
      setRenvoyerResult({
        nb_crees: res.nb_crees, nb_modifies: res.nb_modifies,
        nb_ignores: res.nb_ignores, nb_total_traites: res.nb_total_traites,
        recalcul_effectue: res.recalcul_effectue,
      });
      dispatch(ocrApi.util.invalidateTags(['ReleveCorrection']));
      setMessage({ type: 'success', text: `${res.nb_total_traites} note(s) renvoyée(s) en base.` });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.data?.error || error?.data?.message || 'Erreur lors du renvoi' });
    }
  };

  // Raccourcis clavier
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (enEdition) return;
      const t = e.target as HTMLElement;
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') return;
      switch (e.key) {
        case 'ArrowDown': case 'j': e.preventDefault(); goNext(); break;
        case 'ArrowUp': case 'k': e.preventDefault(); goPrev(); break;
        case 'Enter': e.preventDefault(); handleConfirmer(); break;
        case 'e': case 'E': e.preventDefault(); setEnEdition(true); setTimeout(() => inputRef.current?.focus(), 50); break;
        case '+': setZoomLevel(z => Math.min(300, z + 25)); break;
        case '-': setZoomLevel(z => Math.max(50, z - 25)); break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enEdition, goNext, goPrev, handleConfirmer]);

  if (isLoading) return <div className="flex items-center justify-center h-screen"><Loader className="animate-spin text-blue-600" size={48} /></div>;

  return (
    <div className="h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 px-4 py-2 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/ocr/generation-correction-v2')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><ChevronLeft size={20} /></button>
            <div className="min-w-0">
              <h1 className="text-xs font-bold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                <Eye size={15} />
                <span className="font-mono">{releve?.code_releve}</span>
                {releve?.matiere_code && (
                  <span className="px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-[9px] font-semibold">
                    {releve.matiere_code}
                  </span>
                )}
                {releve?.matiere_nom && (
                  <span className="text-gray-600 dark:text-gray-400 font-normal truncate max-w-[280px]" title={releve.matiere_nom}>
                    {releve.matiere_nom}
                  </span>
                )}
              </h1>
              <div className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400 flex-wrap mt-0.5">
                {releve?.niveau && (
                  <span className="px-1 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-semibold">{releve.niveau}</span>
                )}
                {releve?.classes && (
                  <span className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded truncate max-w-[200px]" title={releve.classes}>
                    {releve.classes}
                  </span>
                )}
                {!releve?.classes && releve?.option && (
                  <span className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">{releve.option}</span>
                )}
                {releve?.enseignant_nom && (
                  <span className="flex items-center gap-0.5">
                    <span className="text-gray-400">Formateur :</span>
                    <strong className="text-gray-700 dark:text-gray-300">{releve.enseignant_nom}</strong>
                  </span>
                )}
                {releve?.semestre && (
                  <span className="text-gray-400">· {releve.semestre} {releve.annee_academique}</span>
                )}
                {releve?.type_examen === 'rattrapage' && (
                  <span className="px-1 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded font-semibold">Rattrapage</span>
                )}
                <span className="text-gray-400">· {lignesVivantes.length} lignes</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {releve?.fichier_scan && (
              <button onClick={() => setShowFullImage(true)} className="flex items-center gap-1 px-2 py-1 text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"><FileImage size={12} /> Doc</button>
            )}
            <button onClick={() => setShowRaccourcis(!showRaccourcis)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700" title="Raccourcis"><Keyboard size={14} /></button>
            <label className="flex items-center gap-1 text-[10px] cursor-pointer select-none">
              <input type="checkbox" checked={filtreAVerifier} onChange={(e) => setFiltreAVerifier(e.target.checked)} className="rounded w-3 h-3" />
              <span className="dark:text-gray-300">A vérifier</span>
              {nbAVerifier > 0 && <span className="px-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 rounded text-[9px] font-bold">{nbAVerifier}</span>}
            </label>
            <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 rounded text-[10px]">{nbValidees}/{lignesVivantes.length}</span>
            <button onClick={() => { setNbCodesPapier(String(lignesVivantes.length)); setShowConfirmTerminer(true); }} disabled={validerToutLoading || nbAVerifier > 0}
              className="flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-[10px] font-medium disabled:opacity-50">
              {validerToutLoading ? <Loader size={11} className="animate-spin" /> : <FileCheck size={11} />} Terminer
            </button>
            {/* Renvoyer les notes en BDD : visible quand le relevé est validé */}
            {releve?.status === 'valide' && (
              <button onClick={() => { setRenvoyerEcraser(false); setRenvoyerResult(null); setShowRenvoyerModal(true); }}
                disabled={renvoyerLoading}
                className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-medium disabled:opacity-50"
                title="Renvoyer toutes les notes du relevé vers la base de données (utile si certaines notes manquent)">
                {renvoyerLoading ? <Loader size={11} className="animate-spin" /> : <RefreshCw size={11} />} Renvoyer notes
              </button>
            )}
          </div>
        </div>
        {showRaccourcis && (
          <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded text-[10px] text-gray-600 dark:text-gray-400 flex flex-wrap gap-3">
            <span><kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-[9px] font-mono">↑↓</kbd> Navigation</span>
            <span><kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-[9px] font-mono">Entrée</kbd> Confirmer</span>
            <span><kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-[9px] font-mono">E</kbd> Éditer</span>
            <span><kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-[9px] font-mono">Échap</kbd> Annuler</span>
            <span><kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-[9px] font-mono">+/-</kbd> Zoom</span>
          </div>
        )}
        {message && (
          <div className={`mt-1.5 px-2 py-1 rounded flex items-center text-[10px] ${message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'}`}>
            {message.type === 'success' ? <CheckCircle2 size={12} className="mr-1 shrink-0" /> : <AlertTriangle size={12} className="mr-1 shrink-0" />}{message.text}
          </div>
        )}
      </div>

      {/* Contenu */}
      {lignesFiltrees.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md px-4">
            {filtreAVerifier ? (
              <>
                <CheckCircle2 className="mx-auto text-green-500" size={48} />
                <h2 className="mt-3 text-lg font-semibold dark:text-gray-100">Tout est vérifié</h2>
                <button onClick={() => setFiltreAVerifier(false)} className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">Voir tout</button>
              </>
            ) : (
              <>
                {ocrPolling ? (
                  <>
                    <Loader className="mx-auto text-indigo-500 animate-spin" size={48} />
                    <h2 className="mt-3 text-lg font-semibold dark:text-gray-100">
                      OCR en cours... {ocrElapsed}s
                    </h2>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Le traitement prend en moyenne <strong>50 à 90 secondes</strong> selon la taille du relevé.
                      Laissez cette page ouverte, les lignes apparaîtront automatiquement.
                    </p>
                    <div className="mt-4 w-full max-w-xs mx-auto bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-indigo-600 h-2 transition-all duration-500 ease-linear"
                        style={{ width: `${Math.min(100, (ocrElapsed / 90) * 100)}%` }}
                      />
                    </div>
                    <p className="mt-3 text-[11px] text-gray-400">
                      (Détection code-barres + analyse notes manuscrites via Google Cloud Vision)
                    </p>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="mx-auto text-orange-500" size={48} />
                    <h2 className="mt-3 text-lg font-semibold dark:text-gray-100">Aucune ligne détectée</h2>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      L'OCR n'a trouvé aucune note sur ce relevé. Cela arrive parfois lors du premier lancement.
                      Essayez de relancer le traitement.
                    </p>
                    <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                      <button
                        onClick={handleRelancerOCR}
                        disabled={ocrLoading || ocrPolling}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
                      >
                        <RefreshCw size={14} />
                        Réessayer l'OCR
                      </button>
                      <button
                        onClick={() => refetch()}
                        disabled={ocrLoading || ocrPolling}
                        className="inline-flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
                        title="Rafraîchir sans relancer l'OCR"
                      >
                        <RefreshCw size={14} /> Rafraîchir
                      </button>
                    </div>
                    <p className="mt-3 text-xs text-gray-400">
                      Si après 2-3 tentatives aucun résultat, retournez ce relevé en cours et re-uploadez un scan de meilleure qualité.
                    </p>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">

          {/* ═══ PANNEAU CENTRAL : Image + Infos + Actions (tout scrollable ensemble) ═══ */}
          <div className="flex-1 overflow-y-auto bg-gray-800 dark:bg-gray-950">
            {ligneCourante && (
              <>
                {/* Zoom controls */}
                <div className="sticky top-0 z-10 flex items-center justify-between px-3 py-1.5 bg-gray-900/90 backdrop-blur-sm">
                  <span className="text-white text-xs">Ligne {ligneCourante.numero_ligne}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setZoomLevel(Math.max(50, zoomLevel - 25))} className="p-0.5 text-white/70 hover:text-white"><ZoomOut size={14} /></button>
                    <span className="text-white/70 text-[10px] w-7 text-center">{zoomLevel}%</span>
                    <button onClick={() => setZoomLevel(Math.min(300, zoomLevel + 25))} className="p-0.5 text-white/70 hover:text-white"><ZoomIn size={14} /></button>
                  </div>
                </div>

                {/* Image — chargement à la demande */}
                <LigneImage
                  releveId={parseInt(releveId!)}
                  ligneId={ligneCourante.id}
                  numeroLigne={ligneCourante.numero_ligne}
                  inlineImage={ligneCourante.image_base64}
                  zoom={zoomLevel}
                />


                {/* ── Résultat OCR (juste sous l'image, fond sombre) ── */}
                {(() => {
                  const etudInfo = getEtudiantInfo(ligneCourante);
                  return (
                    <div className="bg-gray-900 dark:bg-black px-4 py-2.5 border-t border-gray-700">
                      {/* Ligne 1 : Code → Note + Confiance */}
                      <div className="flex items-center justify-center gap-3">
                        <span className="font-mono text-sm font-bold text-blue-400">{ligneCourante.code_copie_detecte}</span>
                        <span className="text-gray-600">→</span>
                        <span className={`text-2xl font-extrabold ${ligneCourante.note_finale !== null && ligneCourante.note_finale !== undefined ? 'text-green-400' : 'text-red-400'}`}>
                          {ligneCourante.note_finale !== null && ligneCourante.note_finale !== undefined ? `${formatNote(ligneCourante.note_finale)}/20` : 'N/A'}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getConfianceColor(ligneCourante.confiance_manuscrit ?? 0)}`}>
                          {Number(ligneCourante.confiance_manuscrit ?? 0).toFixed(0)}%
                        </span>
                        {ligneCourante.necessite_verification ? <AlertTriangle size={14} className="text-orange-400" /> : <CheckCircle2 size={14} className="text-green-400" />}
                      </div>
                      {/* Ligne 2 : Nom étudiant + Classe */}
                      {etudInfo.exists && (
                        <div className="flex items-center justify-center gap-2 mt-1">
                          <span className="text-xs text-gray-400">{etudInfo.nomComplet}</span>
                          <span className="text-[10px] text-gray-500 font-mono">{etudInfo.matricule}</span>
                          {etudInfo.classe && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-indigo-900/40 text-indigo-300 rounded">{etudInfo.classe}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── Infos étudiant + Actions (fond clair) ── */}
                <div className="bg-white dark:bg-gray-800 px-4 py-3 space-y-3">
                  {/* Étudiant */}
                  {(() => {
                    const etud = getEtudiantInfo(ligneCourante);
                    return (
                      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Copie</span>
                            <div className="font-mono font-bold text-sm text-gray-900 dark:text-gray-100">{ligneCourante.code_copie_detecte}</div>
                          </div>
                          {etud.exists ? (
                            <div className="text-right">
                              <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Étudiant associé</span>
                              <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">{etud.nomComplet}</div>
                              <div className="flex items-center justify-end gap-2 mt-0.5">
                                <span className="text-[10px] text-gray-400 font-mono">{etud.matricule}</span>
                                {etud.classe && (
                                  <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded font-medium">{etud.classe}</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-right">
                              <span className="text-[10px] text-orange-500">Copie non associée</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Actions */}
                  <div className="flex items-center justify-between">
                    {/* Navigation */}
                    <div className="flex items-center gap-2">
                      <button onClick={goPrev} disabled={selectedIndex === 0} className="px-2.5 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded disabled:opacity-30">← Préc.</button>
                      <span className="text-xs text-gray-400 font-medium">{selectedIndex + 1}/{lignesFiltrees.length}</span>
                      <button onClick={goNext} disabled={selectedIndex >= lignesFiltrees.length - 1} className="px-2.5 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded disabled:opacity-30">Suiv. →</button>
                    </div>

                    {/* Boutons d'action */}
                    <div className="flex items-center gap-2">
                      {enEdition ? (
                        <>
                          <input ref={inputRef} type="number" step="0.25" min="0" max="20" value={editNote}
                            onChange={(e) => setEditNote(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSauvegarder(); if (e.key === 'Escape') setEnEdition(false); }}
                            className="w-16 border border-blue-400 rounded px-2 py-1 text-sm font-bold dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400" />
                          <span className="text-xs text-gray-400">/20</span>
                          <button onClick={handleSauvegarder} disabled={loading} className="flex items-center gap-1 px-2.5 py-1 bg-green-600 text-white rounded text-xs disabled:opacity-60">
                            {loading ? <Loader size={11} className="animate-spin" /> : <Save size={11} />} OK
                          </button>
                          <button onClick={() => setEnEdition(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded"><X size={14} /></button>
                        </>
                      ) : (
                        <>
                          {ligneCourante.necessite_verification && (
                            <button onClick={handleConfirmer} disabled={loading}
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-semibold disabled:opacity-60 shadow-sm">
                              {loading ? <Loader size={12} className="animate-spin" /> : <ThumbsUp size={12} />} Confirmer
                            </button>
                          )}
                          <button onClick={() => { setEnEdition(true); setTimeout(() => inputRef.current?.focus(), 50); }}
                            className="px-2.5 py-1.5 text-xs text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20">
                            Corriger
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ═══ LISTE DES LIGNES (droite, plus large) ═══ */}
          <div id="ligne-list-container" className="w-80 bg-white dark:bg-gray-800 border-l dark:border-gray-700 overflow-y-auto shrink-0">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 z-10">
              {/* Ligne 1 : compteur + boutons ajouter / manquants */}
              <div className="px-3 pt-2 pb-1 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Lignes ({lignesFiltrees.length})
                  <span className="text-gray-400 ml-1">· total {lignesVivantes.length}</span>
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      if (releveId) fetchCodesManquants(parseInt(releveId));
                      setShowCodesManquants(true);
                    }}
                    className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded text-[10px] font-semibold hover:bg-amber-200 dark:hover:bg-amber-900/50"
                    title="Voir les codes attendus non détectés par l'OCR"
                  >
                    <AlertTriangle size={11} /> Manquants
                  </button>
                  <button
                    onClick={() => { setShowAjoutManuel(true); setTimeout(() => ajoutInputRef.current?.focus(), 100); }}
                    className="flex items-center gap-1 px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded text-[10px] font-semibold hover:bg-teal-200 dark:hover:bg-teal-900/50"
                    title="Ajouter manuellement une copie (scanner ou saisir le code)"
                  >
                    <Plus size={12} /> Ajouter
                  </button>
                </div>
              </div>
              {/* Ligne 2 : barre de recherche */}
              <div className="px-3 pb-2">
                <div className="relative">
                  <input
                    type="text"
                    value={rechercheCode}
                    onChange={e => setRechercheCode(e.target.value)}
                    placeholder="Rechercher par code, nom, matricule, classe..."
                    className="w-full px-2 py-1 text-[11px] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                  />
                  {rechercheCode && (
                    <button
                      onClick={() => setRechercheCode('')}
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                      title="Effacer"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                {rechercheCode && (
                  <div className="mt-1 text-[9px] text-gray-400">
                    {lignesFiltrees.length} résultat{lignesFiltrees.length > 1 ? 's' : ''} sur {lignesVivantes.length}
                  </div>
                )}
              </div>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700/30">
              {lignesFiltrees.length === 0 && rechercheCode && (
                <div className="px-3 py-4 text-center text-xs text-gray-400">
                  Aucun résultat pour "<strong>{rechercheCode}</strong>"
                </div>
              )}
              {lignesFiltrees.map((l, idx) => {
                const etud = getEtudiantInfo(l);
                return (
                  <div
                    key={l.id}
                    id={`ligne-item-${idx}`}
                    onClick={() => setSelectedIndex(idx)}
                    className={`px-3 py-2 cursor-pointer transition-colors ${
                      idx === selectedIndex
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-3 border-blue-500'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/30 border-l-3 border-transparent'
                    }`}
                  >
                    {/* Ligne 1 : Code + Note + Statut */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400 w-5">{l.numero_ligne}</span>
                        <span className="font-mono text-xs font-bold text-gray-800 dark:text-gray-200">{l.code_copie_detecte}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-sm font-extrabold ${l.note_finale !== null && l.note_finale !== undefined ? 'text-gray-900 dark:text-gray-100' : 'text-red-500'}`}>
                          {formatNote(l.note_finale)}
                        </span>
                        <span className="text-[9px] text-gray-400">/20</span>
                        <span className={`px-1 py-0.5 rounded text-[8px] font-medium ${getConfianceColor(l.confiance_manuscrit ?? 0)}`}>
                          {Number(l.confiance_manuscrit ?? 0).toFixed(0)}%
                        </span>
                        {l.necessite_verification ? <AlertTriangle size={10} className="text-orange-500" /> : <CheckCircle2 size={10} className="text-green-500" />}
                      </div>
                    </div>
                    {/* Ligne 2 : Étudiant + Classe */}
                    {etud.exists && (
                      <div className="flex items-center justify-between mt-0.5 pl-7">
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate flex-1">
                          {etud.nomComplet}
                        </span>
                        <div className="flex items-center gap-1 ml-1 shrink-0">
                          {etud.classe && (
                            <span className="text-[8px] px-1 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 rounded">{etud.classe}</span>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Actions rapides sur la ligne sélectionnée */}
                    {idx === selectedIndex && (
                      <div className="mt-1 pl-7 flex items-center gap-1 flex-wrap">
                        {l.necessite_verification && (
                          <button onClick={(e) => { e.stopPropagation(); handleConfirmer(); }} disabled={loading}
                            className="flex items-center gap-1 px-2 py-0.5 bg-green-600 hover:bg-green-700 text-white rounded text-[10px] font-medium disabled:opacity-60">
                            {loading ? <Loader size={10} className="animate-spin" /> : <ThumbsUp size={10} />} Confirmer
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setLignePourEdition(l); setNouveauCode(l.code_copie_detecte || ''); }}
                          className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 rounded text-[10px] font-medium"
                          title="Modifier le code (correction OCR)"
                        >
                          <Edit3 size={10} /> Modifier code
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setLignePourSuppression(l); }}
                          className="flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 rounded text-[10px] font-medium"
                          title="Supprimer (code en trop, doublon)"
                        >
                          <Trash2 size={10} /> Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Modal document complet */}
      {showFullImage && releve?.fichier_scan && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-2 flex justify-between items-center z-10">
              <span className="text-sm font-semibold dark:text-gray-200">Document scanné</span>
              <button onClick={() => setShowFullImage(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><X size={18} className="text-gray-500" /></button>
            </div>
            <div className="p-4">
              <iframe src={`${window.location.hostname === 'localhost' ? 'http://localhost:8000' : ''}/media/${releve.fichier_scan}`} className="w-full h-[80vh] border rounded" title="Document scanné" />
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Suppression Ligne */}
      {lignePourSuppression && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
              <Trash2 size={18} className="text-red-600" />
              Supprimer cette ligne ?
            </h3>
            <div className="my-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded">
              <p className="text-sm text-red-800 dark:text-red-300">
                Code : <strong className="font-mono">{lignePourSuppression.code_copie_detecte}</strong>
              </p>
              {getEtudiantInfo(lignePourSuppression).exists && (
                <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                  Étudiant : {getEtudiantInfo(lignePourSuppression).nomComplet}
                </p>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              La ligne sera définitivement supprimée du relevé. Utile pour retirer un code en trop ou doublon.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setLignePourSuppression(null)} disabled={supprimerLoading}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                Annuler
              </button>
              <button onClick={handleSupprimerLigne} disabled={supprimerLoading}
                className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50">
                {supprimerLoading ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modification Code */}
      {lignePourEdition && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
              <Edit3 size={18} className="text-blue-600" />
              Modifier le code de la copie
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Utile quand l'OCR a mal lu un ou plusieurs caractères. Le nouveau code sera recherché
              dans la base — si une copie correspond, elle sera automatiquement associée.
            </p>
            <div className="mb-3">
              <label className="text-xs text-gray-500 block mb-1">Code détecté (actuel)</label>
              <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700 font-mono text-sm text-gray-700 dark:text-gray-300 rounded">
                {lignePourEdition.code_copie_detecte}
              </div>
            </div>
            <div className="mb-4">
              <label className="text-xs text-gray-500 block mb-1">Nouveau code *</label>
              <input
                type="text"
                value={nouveauCode}
                onChange={e => setNouveauCode(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && nouveauCode.trim()) handleModifierCode(); }}
                placeholder="Ex: SOR12345"
                autoFocus
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setLignePourEdition(null); setNouveauCode(''); }} disabled={modifierLoading}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                Annuler
              </button>
              <button onClick={handleModifierCode} disabled={modifierLoading || !nouveauCode.trim() || nouveauCode.trim() === lignePourEdition.code_copie_detecte}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
                {modifierLoading ? <Loader size={14} className="animate-spin" /> : <Edit3 size={14} />}
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajout manuel (scanner code + preview + note) */}
      {showAjoutManuel && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Plus size={18} className="text-teal-600" />
                Ajouter une copie manquante
              </h3>
              <button
                onClick={() => { setShowAjoutManuel(false); setAjoutCode(''); setAjoutNote(''); setAjoutAbsent(false); setAjoutPreview(null); setAjoutPreviewError(null); }}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Scannez ou saisissez le code-barre <strong>complet</strong>. L'étudiant est détecté automatiquement — il ne reste qu'à saisir la note.
            </p>

            {/* Champ code */}
            <div className="mb-3">
              <label className="text-xs text-gray-500 block mb-1">Code de la copie *</label>
              <div className="relative">
                <input
                  ref={ajoutInputRef}
                  type="text"
                  value={ajoutCode}
                  onChange={e => setAjoutCode(e.target.value)}
                  placeholder="Scannez ou saisissez..."
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 pr-9 text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                />
                {ajoutPreviewLoading && (
                  <Loader size={14} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-teal-500" />
                )}
                {!ajoutPreviewLoading && ajoutPreview && (
                  <CheckCircle2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
                )}
                {!ajoutPreviewLoading && ajoutPreviewError && (
                  <AlertTriangle size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                )}
              </div>
              {ajoutCode.trim() && ajoutCode.trim().length < 6 && (
                <p className="mt-1 text-[10px] text-gray-400 italic">
                  Continuez de saisir le code complet...
                </p>
              )}
            </div>

            {/* Preview : étudiant détecté */}
            {ajoutPreview && (
              <div className="mb-3 p-3 rounded border bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                <div className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-gray-900 dark:text-gray-100">
                      {ajoutPreview.etudiant_nom || <span className="italic text-red-500">Copie non associée à un étudiant</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-600 dark:text-gray-400 flex-wrap">
                      {ajoutPreview.etudiant_matricule && <span className="font-mono">{ajoutPreview.etudiant_matricule}</span>}
                      {ajoutPreview.etudiant_classe && (
                        <span className="px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded">
                          {ajoutPreview.etudiant_classe}
                        </span>
                      )}
                      {ajoutPreview.etudiant_niveau && (
                        <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                          {ajoutPreview.etudiant_niveau}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Matière : <strong>{ajoutPreview.matiere_nom || '—'}</strong>
                      {ajoutPreview.matiere_code && <span className="ml-1 font-mono text-gray-400">({ajoutPreview.matiere_code})</span>}
                    </div>
                    {releve?.matiere && ajoutPreview.matiere && ajoutPreview.matiere !== releve.matiere && (
                      <div className="mt-2 p-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded text-[11px] flex items-start gap-1">
                        <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                        <span>
                          Cette copie appartient à une <strong>autre matière</strong> ({ajoutPreview.matiere_nom}).
                          Elle sera réassociée à <strong>{releve.matiere_nom}</strong> automatiquement lors de l'ajout.
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Erreur : code introuvable */}
            {ajoutPreviewError && (
              <div className="mb-3 p-3 rounded border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{ajoutPreviewError}</span>
              </div>
            )}

            {/* Section note — activée uniquement si preview OK */}
            <div className={!ajoutPreview ? 'opacity-40 pointer-events-none' : ''}>
              <div className="mb-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ajoutAbsent}
                    onChange={e => { setAjoutAbsent(e.target.checked); if (e.target.checked) setAjoutNote(''); }}
                    className="rounded"
                  />
                  <span>Étudiant absent (pas de note)</span>
                </label>
              </div>

              {!ajoutAbsent && (
                <div className="mb-4">
                  <label className="text-xs text-gray-500 block mb-1">Note /20 *</label>
                  <input
                    ref={ajoutNoteRef}
                    type="number"
                    min="0"
                    max="20"
                    step="0.25"
                    value={ajoutNote}
                    onChange={e => setAjoutNote(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && ajoutCode.trim() && (ajoutAbsent || ajoutNote.trim()) && ajoutPreview) handleAjouterManuel(); }}
                    placeholder="Ex: 15.50"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-lg font-bold bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                  />
                </div>
              )}
            </div>

            {/* Boutons */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowAjoutManuel(false); setAjoutCode(''); setAjoutNote(''); setAjoutAbsent(false); setAjoutPreview(null); setAjoutPreviewError(null); }}
                disabled={ajouterLoading}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Fermer
              </button>
              <button
                onClick={handleAjouterManuel}
                disabled={
                  ajouterLoading ||
                  !ajoutPreview ||
                  !ajoutCode.trim() ||
                  (!ajoutAbsent && !ajoutNote.trim())
                }
                className="flex items-center gap-2 px-3 py-1.5 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:opacity-50"
              >
                {ajouterLoading ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
                Ajouter
              </button>
            </div>

            <p className="mt-3 text-[10px] text-gray-400 dark:text-gray-500">
              Astuce : après l'ajout, le champ code reprend le focus pour scanner la suivante en rafale.
            </p>
          </div>
        </div>
      )}

      {/* Modal Codes manquants */}
      {showCodesManquants && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-600" />
                Codes manquants (non détectés par l'OCR)
              </h3>
              <button onClick={() => setShowCodesManquants(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              {codesManquantsLoading && (
                <div className="flex items-center justify-center py-8 gap-2 text-gray-500">
                  <Loader size={18} className="animate-spin" />
                  Recherche des codes manquants...
                </div>
              )}

              {!codesManquantsLoading && codesManquantsData && (
                <>
                  <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                    <div className="p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                      <div className="text-[10px] text-blue-600 dark:text-blue-400">Attendus</div>
                      <div className="text-xl font-bold text-blue-700 dark:text-blue-300">{codesManquantsData.total_attendu}</div>
                    </div>
                    <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                      <div className="text-[10px] text-green-600 dark:text-green-400">Présents (OCR)</div>
                      <div className="text-xl font-bold text-green-700 dark:text-green-300">{codesManquantsData.total_present}</div>
                    </div>
                    <div className={`p-2 border rounded ${codesManquantsData.nb_manquants > 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`}>
                      <div className={`text-[10px] ${codesManquantsData.nb_manquants > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`}>Manquants</div>
                      <div className={`text-xl font-bold ${codesManquantsData.nb_manquants > 0 ? 'text-red-700 dark:text-red-300' : 'text-gray-600'}`}>{codesManquantsData.nb_manquants}</div>
                    </div>
                  </div>

                  {codesManquantsData.nb_manquants === 0 ? (
                    <div className="text-center py-6 text-green-600 dark:text-green-400">
                      <CheckCircle2 className="mx-auto mb-2" size={32} />
                      <p className="text-sm font-medium">Tous les codes attendus sont présents</p>
                      <p className="text-xs text-gray-400 mt-1">L'OCR a bien détecté toutes les copies de la matière.</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        Ces copies existent en base mais n'ont pas été détectées par l'OCR. Cliquez sur un code pour pré-remplir le formulaire d'ajout :
                      </p>
                      <div className="border border-gray-200 dark:border-gray-700 rounded overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                            <tr>
                              <th className="text-left px-2 py-1.5 font-semibold text-gray-600 dark:text-gray-300">Code</th>
                              <th className="text-left px-2 py-1.5 font-semibold text-gray-600 dark:text-gray-300">Matricule</th>
                              <th className="text-left px-2 py-1.5 font-semibold text-gray-600 dark:text-gray-300">Nom</th>
                              <th className="text-left px-2 py-1.5 font-semibold text-gray-600 dark:text-gray-300">Classe</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/40">
                            {codesManquantsData.codes_manquants.map(m => (
                              <tr
                                key={m.code}
                                className="hover:bg-amber-50 dark:hover:bg-amber-900/10 cursor-pointer transition-colors"
                                onClick={() => {
                                  setAjoutCode(m.code);
                                  setShowCodesManquants(false);
                                  setShowAjoutManuel(true);
                                  setTimeout(() => ajoutInputRef.current?.focus(), 150);
                                }}
                                title="Cliquer pour ajouter ce code"
                              >
                                <td className="px-2 py-1.5 font-mono text-[11px] font-bold text-gray-800 dark:text-gray-200">{m.code}</td>
                                <td className="px-2 py-1.5 font-mono text-gray-500">{m.matricule || '—'}</td>
                                <td className="px-2 py-1.5">{m.nom || <span className="italic text-gray-400">Non associé</span>}</td>
                                <td className="px-2 py-1.5">
                                  {m.classe && (
                                    <span className="px-1 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-[9px]">
                                      {m.classe}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-between items-center p-3 border-t dark:border-gray-700">
              <button
                onClick={() => { if (releveId) fetchCodesManquants(parseInt(releveId)); }}
                disabled={codesManquantsLoading}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50"
              >
                <RefreshCw size={12} className={codesManquantsLoading ? 'animate-spin' : ''} />
                Rafraîchir
              </button>
              <button
                onClick={() => setShowCodesManquants(false)}
                className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Terminer — vérification nombre codes */}
      {showConfirmTerminer && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
              <FileCheck size={18} className="text-green-600" />
              Vérification avant validation finale
            </h3>
            <div className="my-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded">
              <p className="text-sm text-gray-800 dark:text-gray-200">
                Le relevé contient actuellement :
              </p>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300 mt-1">
                {lignesVivantes.length} code{lignesVivantes.length > 1 ? 's' : ''} traité{lignesVivantes.length > 1 ? 's' : ''}
              </p>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Comptez les codes-barres présents sur votre relevé <strong>papier</strong> et saisissez le nombre ci-dessous
              pour vérifier la correspondance.
            </p>
            <div className="mb-4">
              <label className="text-xs text-gray-500 block mb-1">Nombre de codes sur le relevé papier</label>
              <input
                type="number"
                min="0"
                value={nbCodesPapier}
                onChange={e => setNbCodesPapier(e.target.value)}
                autoFocus
                className={`w-full border rounded px-3 py-2 text-lg font-bold text-center ${
                  nbCodesPapier && parseInt(nbCodesPapier) !== lignesVivantes.length
                    ? 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                }`}
              />
              {nbCodesPapier && parseInt(nbCodesPapier) !== lignesVivantes.length && (
                <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded flex items-start gap-2">
                  <AlertTriangle size={14} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-xs text-red-700 dark:text-red-300">
                    Écart de <strong>{Math.abs(parseInt(nbCodesPapier) - lignesVivantes.length)}</strong> code(s).
                    Vérifiez avant de valider (utilisez ✏️ pour corriger un code, 🗑 pour supprimer, ➕ pour ajouter).
                  </span>
                </div>
              )}
              {nbCodesPapier && parseInt(nbCodesPapier) === lignesVivantes.length && (
                <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-green-600" />
                  <span className="text-xs text-green-700 dark:text-green-300">
                    Nombres identiques — prêt à valider.
                  </span>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowConfirmTerminer(false)} disabled={validerToutLoading}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                Retour
              </button>
              <button
                onClick={async () => { setShowConfirmTerminer(false); await handleValiderTout(); }}
                disabled={validerToutLoading || !nbCodesPapier || parseInt(nbCodesPapier) !== lignesVivantes.length}
                className="flex items-center gap-2 px-4 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50">
                {validerToutLoading ? <Loader size={14} className="animate-spin" /> : <FileCheck size={14} />}
                Valider et enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Renvoyer les notes en BDD */}
      {showRenvoyerModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RefreshCw className="text-blue-600" size={18} />
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  Renvoyer les notes en base de données
                </h3>
              </div>
              <button onClick={() => setShowRenvoyerModal(false)} disabled={renvoyerLoading}
                className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              {!renvoyerResult && (
                <>
                  <p className="text-gray-700 dark:text-gray-300">
                    Cette action va re-injecter <strong>toutes les notes du relevé OCR</strong> validées
                    dans la table des notes d'examens. Utile si certaines notes ne sont pas apparues correctement.
                  </p>
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-amber-700 dark:text-amber-300">
                        Si une note existe déjà pour un étudiant + matière, que faire ?
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className={`flex items-start gap-2 p-2 rounded cursor-pointer ${!renvoyerEcraser ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-300' : 'border border-gray-200 dark:border-gray-600'}`}>
                      <input type="radio" checked={!renvoyerEcraser}
                        onChange={() => setRenvoyerEcraser(false)} className="mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          Conserver les notes existantes
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Les notes manuelles déjà saisies sont préservées. Seules les notes manquantes seront créées.
                          <span className="text-green-600 font-medium"> (recommandé)</span>
                        </div>
                      </div>
                    </label>
                    <label className={`flex items-start gap-2 p-2 rounded cursor-pointer ${renvoyerEcraser ? 'bg-red-50 dark:bg-red-900/20 border border-red-300' : 'border border-gray-200 dark:border-gray-600'}`}>
                      <input type="radio" checked={renvoyerEcraser}
                        onChange={() => setRenvoyerEcraser(true)} className="mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          Écraser toutes les notes
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Remplace les notes existantes par celles du relevé OCR.
                          <span className="text-red-600 font-medium"> Attention : les modifications manuelles seront perdues.</span>
                        </div>
                      </div>
                    </label>
                  </div>
                </>
              )}

              {renvoyerResult && (
                <div className="space-y-2">
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-300 font-medium mb-2">
                      <CheckCircle2 size={16} /> Opération terminée
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-white dark:bg-gray-800 rounded p-2">
                        <div className="text-gray-500">Notes créées</div>
                        <div className="font-bold text-green-700 dark:text-green-400 text-lg">{renvoyerResult.nb_crees}</div>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded p-2">
                        <div className="text-gray-500">Notes modifiées</div>
                        <div className="font-bold text-blue-700 dark:text-blue-400 text-lg">{renvoyerResult.nb_modifies}</div>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded p-2">
                        <div className="text-gray-500">Ignorées (préservées)</div>
                        <div className="font-bold text-gray-700 dark:text-gray-300 text-lg">{renvoyerResult.nb_ignores}</div>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded p-2">
                        <div className="text-gray-500">Recalculs effectués</div>
                        <div className="font-bold text-purple-700 dark:text-purple-400 text-lg">{renvoyerResult.recalcul_effectue}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              {renvoyerResult ? (
                <button onClick={() => { setShowRenvoyerModal(false); setRenvoyerResult(null); }}
                  className="px-4 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded">
                  Fermer
                </button>
              ) : (
                <>
                  <button onClick={() => setShowRenvoyerModal(false)} disabled={renvoyerLoading}
                    className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                    Annuler
                  </button>
                  <button onClick={handleRenvoyerNotes} disabled={renvoyerLoading}
                    className={`flex items-center gap-2 px-4 py-1.5 text-white text-sm rounded ${
                      renvoyerEcraser ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                    } disabled:opacity-50`}>
                    {renvoyerLoading ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Confirmer
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── LigneImage : charge l'image base64 d'une ligne à la demande ──
// Évite d'envoyer toutes les images dans le payload de liste (gain ~10-15 MB)
const LigneImage: React.FC<{
  releveId: number;
  ligneId: number;
  numeroLigne: number;
  inlineImage?: string;
  zoom: number;
}> = ({ releveId, ligneId, numeroLigne, inlineImage, zoom }) => {
  // Si l'image est déjà dans la payload (ex: ligne fraîchement ajoutée), l'utiliser
  const hasInline = !!(inlineImage && inlineImage.length > 10);

  const { data, isFetching, error, refetch } = useGetImageLigneOCRV2Query(
    { releveId, ligneId },
    { skip: hasInline || ligneId < 0 }
  );

  // Auto-retry transitoires (réseau, 5xx) jusqu'à 2 fois, avec délai croissant.
  // 404 = ligne réellement absente → pas de retry.
  const [autoRetries, setAutoRetries] = useState(0);
  const errStatus = (error as any)?.status;
  const isTransient = !!error && errStatus !== 404 && errStatus !== 'PARSING_ERROR';

  useEffect(() => {
    if (!isTransient || autoRetries >= 2) return;
    const delay = 600 * (autoRetries + 1); // 600ms puis 1200ms
    const t = setTimeout(() => {
      setAutoRetries(n => n + 1);
      refetch();
    }, delay);
    return () => clearTimeout(t);
  }, [isTransient, autoRetries, refetch]);

  // Reset du compteur de retry si on change de ligne
  useEffect(() => { setAutoRetries(0); }, [ligneId]);

  // Sur 404 : cache RTK obsolète (les IDs ont changé après un re-traitement OCR).
  // On invalide la liste des lignes pour forcer un refetch — la ligne disparaîtra
  // de l'UI si elle est vraiment absente, ou l'UI utilisera le bon ID sinon.
  // Fix 2026-08-25 : sans ça, les vignettes restent bloquées sur "Image introuvable"
  // et l'utilisateur doit refresh manuellement toute la page.
  const dispatch = useDispatch();
  const invalidateFiredRef = useRef<number | null>(null);
  useEffect(() => {
    if (errStatus === 404 && invalidateFiredRef.current !== ligneId) {
      invalidateFiredRef.current = ligneId;
      try {
        dispatch(ocrApiV2.util.invalidateTags([{ type: 'LignesOCRV2', id: 'LIST' }]));
      } catch { /* ignore */ }
    }
  }, [errStatus, ligneId, dispatch]);

  const image = hasInline ? inlineImage : (data?.image_base64 || '');

  if (isFetching && !hasInline) {
    return (
      <div className="flex items-center justify-center h-24 text-gray-400 text-xs gap-2 px-3 py-2">
        <Loader size={14} className="animate-spin" />
        Chargement de l'image{autoRetries > 0 ? ` (tentative ${autoRetries + 1})...` : '...'}
      </div>
    );
  }
  if (error) {
    const estIntrouvable = errStatus === 404;
    return (
      <div className="flex flex-col items-center justify-center h-24 text-red-400 text-xs px-3 py-2 gap-2">
        <span>
          {estIntrouvable
            ? 'Image introuvable (ligne peut-être supprimée)'
            : `Erreur de chargement${errStatus ? ` (${errStatus})` : ''}`}
        </span>
        {!estIntrouvable && (
          <button
            type="button"
            onClick={() => { setAutoRetries(0); refetch(); }}
            className="flex items-center gap-1 px-2 py-0.5 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded text-[10px]"
          >
            <RefreshCw size={10} /> Réessayer
          </button>
        )}
      </div>
    );
  }
  if (!image) {
    return <div className="flex items-center justify-center h-24 text-gray-500 text-sm">Aucune image</div>;
  }
  return (
    <div className="flex justify-center px-3 py-2">
      <img
        src={`data:image/png;base64,${image}`}
        alt={`Ligne ${numeroLigne}`}
        style={{ width: `${zoom}%` }}
        className="border border-gray-600 rounded"
      />
    </div>
  );
};

export default VerificationOCRCorrectionV2;
